import React, { createContext, PropsWithChildren, useEffect, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useColorApi } from '../../../api/core-service/wsc/colors/useColorApi';
import {
  ConfigurationData,
  useConfigurationApi,
} from '../../../api/simple-project-service/configurations/useConfigurationApi';
import { ProductParameter } from '../../../interfaces/Parameter';
import { articleService } from '../../../services/articleService';
import { addNotifications } from '../../../store/notifications/notifications.actions';
import { enableInteractions } from '../../../store/suppressInteractions/suppressInteractions.actions';
import { NotificationFactory } from '../../../utils/factories/notificationFactory';
import { Action, configuratorActions } from './configuratorActions';
import { configuratorReducer } from './configuratorReducer';

export interface ConfiguratorContextState {
  /**
   * Current configuration data from mongo-db as it is
   */
  configurationData: Nullable<ConfigurationData>;
  /**
   * Current configuration from project-service
   */
  currentConfiguration: Nullable<SimpleProjectServiceAPI.ConfigurationDto>;
  /**
   * Last not expected error from API call
   */
  configurationError: any;
  articleDefinitions: Record<string, CoreServiceAPI.ArticleDto>;
  currentRequestInfo: Nullable<SimpleProjectServiceAPI.ConfigurationRequestDto>;
  currentProject: Nullable<SimpleProjectServiceAPI.ProjectDto>;
  currentConfigurations: Nullable<SimpleProjectServiceAPI.ConfigurationDto[]>;
  currentRequests: Nullable<SimpleProjectServiceAPI.ConfigurationRequestDto[]>;
  configurationsData: Nullable<ConfigurationData[]>;
  configurationsViewMode: ConfigurationsViewMode;
  selectedConfigurationIds: string[];
  selectedRequestIds: string[];
  drafts: Nullable<SimpleProjectServiceAPI.DraftConfigurationDto[]>;
  /** storeId as a key */
  validationResults: Record<string, CoreServiceAPI.ValidationResultDto>;
  previouslySelectedParameters: ProductParameter[];
}

type GetConfigurationById = ReturnType<typeof useConfigurationApi>['getConfigurationDataById'];
type ConfiguratorFunctions = { getConfigurationById: GetConfigurationById };

export type ConfiguratorContextProps = [
  ConfiguratorContextState,
  React.Dispatch<Action>,
  ConfiguratorFunctions,
];

export const ConfiguratorStateContext = createContext<ConfiguratorContextProps | null>(null);

export enum ConfigurationsViewMode {
  READ = 'READ',
  SELECTION_FOR_REQUEST = 'SELECTION_FOR_REQUEST',
  SELECTION_FOR_SAP = 'SELECTION_FOR_SAP',
}

export const configuratorContextInitialState: ConfiguratorContextState = {
  configurationData: null,
  currentConfiguration: null,
  configurationError: null,
  articleDefinitions: {},
  currentRequestInfo: null,
  currentProject: null,
  currentConfigurations: null,
  configurationsData: null,
  currentRequests: null,
  configurationsViewMode: ConfigurationsViewMode.READ,
  selectedConfigurationIds: [],
  selectedRequestIds: [],
  drafts: null,
  validationResults: {},
  previouslySelectedParameters: [],
};

const { setCurrentConfiguration, setLastError } = configuratorActions;

export const ConfiguratorStateContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  /* lib hooks */
  const [state, dispatch] = useReducer(configuratorReducer, configuratorContextInitialState);
  const dispatchRedux = useDispatch<any>();

  /* app hooks */
  const { primaryColor, secondaryColor } = useColorApi();
  const {
    getConfigurationDataById: getConfigurationById,
    configurationDataById,
    configurationDataByIdError,
  } = useConfigurationApi();

  /* local states */
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [configurationFunctions] = useState<ConfiguratorFunctions>({
    getConfigurationById,
  });

  useEffect(() => {
    setIsMounted(true);
    dispatch(setCurrentConfiguration(null));
    return () => {
      setIsMounted(false);
    };
  }, []);

  /* temporary solution to set that values to the global articleService instance  */
  useEffect(() => {
    if (primaryColor) {
      articleService.primaryColor = primaryColor;
    }
    if (secondaryColor) {
      articleService.secondaryColor = secondaryColor;
    }
  }, [primaryColor, secondaryColor]);

  /* store configuration data */
  useEffect(() => {
    dispatchRedux(enableInteractions('getConfigurationById'));
    dispatch(configuratorActions.setConfigurationData(configurationDataById));
  }, [configurationDataById, dispatchRedux]);

  /* store configuration data error */
  useEffect(() => {
    if (configurationDataByIdError) {
      dispatchRedux(addNotifications([NotificationFactory.configurationCannotBeOpened()]));
      dispatch(setLastError(configurationDataByIdError));
    }
  }, [configurationDataByIdError, dispatch, dispatchRedux]);

  if (!isMounted) {
    return null;
  }

  return (
    <ConfiguratorStateContext.Provider value={[state, dispatch, configurationFunctions]}>
      {children}
    </ConfiguratorStateContext.Provider>
  );
};
